import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AccessToken_FieldsFragment, useAccessToken_UserUpdateAccessTokenMutation } from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  content: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontWeight: 700,
    fontSize: '24px',
    marginTop: '18px',
    alignSelf: 'center',
  },
  saveButton: {
    borderRadius: '100px',
    maxHeight: '40px',
    marginTop: '40px',
    marginBottom: '20px',
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
  },
}));

type Props = {
  accessToken: AccessToken_FieldsFragment;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
};

type UpdateAccessTokenFormValues = {
  name: string;
};

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Please enter a name')
    .matches(/^[a-zA-Z0-9]+$/, 'Must be only letters and numbers')
    .min(1, 'Minimum characters is 1')
    .max(50, 'Maximum characters is 50'),
});

export default function UpdateAccessTokenForm({ accessToken, onExitClick }: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<UpdateAccessTokenFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: accessToken.name,
    },
  });

  const [userUpdateAccessToken] = useAccessToken_UserUpdateAccessTokenMutation({
    onCompleted: () => {
      showToast({
        message: 'The access token has been updated.',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating access token: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const onSubmit = async (values: UpdateAccessTokenFormValues) => {
    const { name } = values;

    if (
      name !== accessToken.name &&
      !window.confirm(
        'Are you sure you want to change the token name? You will need to update any existing auth connections to the new name.',
      )
    ) {
      return;
    }

    await userUpdateAccessToken({
      variables: {
        input: {
          id: accessToken.id,
          portalId,
          name,
        },
      },
    });
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.content}>
        <Typography className={classes.title}>Edit Access Token</Typography>
        <div>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormTextField
                {...field}
                inputRef={ref}
                label="Token Name"
                errorMessage={errors.name?.message}
              />
            )}
          />
        </div>
        <Button
          type="submit"
          variant="outlined"
          className={classes.saveButton}
          disabled={isSubmitting}
        >
          Save
        </Button>
      </div>
    </form>
  );
}
